import { useEffect, useRef, useState } from 'react'
import { formatarCep } from '@precad/compartilhado/validacao/cep'
import { formatarCnpj } from '@precad/compartilhado/validacao/cnpj'
import { formatarCpf } from '@precad/compartilhado/validacao/cpf'
import { formatarTelefone } from '@precad/compartilhado/validacao/telefone'
import { chamar, enviarArquivos, exigirSucesso } from '../api'
import { isoParaBr } from '@precad/compartilhado/validacao/data'
import type { FichaCadastral } from '../tipos'

/**
 * Ficha cadastral: impressão e envio do assinado.
 *
 * IMPRIME NO MESMO DOCUMENTO, com `@media print` — nunca `window.open`. A aba
 * nova não levaria o cookie de sessão junto de forma confiável, e a ficha
 * chegaria vazia justo na hora de imprimir.
 *
 * O texto legal renderizado é o que ESTE cliente aceitou, na versão que ele
 * leu — vem do servidor com hash, data e IP do aceite. Nada aqui é
 * hard-coded: uma ficha que imprimisse a redação vigente estaria pondo a
 * assinatura da pessoa sob um texto que ela nunca viu.
 *
 * CABE EM UMA FOLHA — e isso é requisito, não estética. A primeira versão saía
 * com duas páginas para um cadastro de dois sócios, a segunda quase vazia. Três
 * causas, todas corrigidas aqui e no `estilo.css`:
 *
 *   1. um campo por LINHA, com rótulo de 10rem. Agora são duas colunas de
 *      pares, e o rótulo cabe em 8,5rem;
 *   2. sócios e documentos como lista de campos — quatro linhas por sócio.
 *      Agora são TABELAS: dois sócios ocupam duas linhas, não oito;
 *   3. `break-inside: avoid` em TODO bloco. O texto legal é o bloco mais alto
 *      da folha, e quando ele não cabia no que restava da página pulava inteiro
 *      para a seguinte — deixando exatamente as "áreas em branco" relatadas. Só
 *      os blocos curtos continuam indivisíveis.
 *
 * Sócio a mais continua empurrando para a segunda folha em algum ponto, e isso
 * é inevitável: são duas linhas de tabela por pessoa. O que não é aceitável é
 * gastar folha com espaço vazio.
 */

function Campo({
  rotulo,
  valor,
  largo,
}: {
  rotulo: string
  valor: string | null | undefined
  /** Ocupa as duas colunas. Para endereço e e-mail, que não caberiam em uma. */
  largo?: boolean
}) {
  return (
    <div className={largo ? 'ficha__campo ficha__campo--largo' : 'ficha__campo'}>
      <span className="ficha__rotulo">{rotulo}</span>
      <span className="ficha__valor">{valor?.trim() ? valor : '—'}</span>
    </div>
  )
}

function Bloco({ titulo, children }: { titulo: string; children: React.ReactNode }) {
  return (
    <section className="ficha__bloco">
      <h2 className="ficha__secao">{titulo}</h2>
      {children}
    </section>
  )
}

export function Ficha({
  aoVoltar,
  logoUrl,
  logoAltura,
}: {
  aoVoltar: () => void
  /** A logo configurada em `precad_Parametro`. A mesma do topo da página. */
  logoUrl?: string | null
  logoAltura?: number
}) {
  const [dados, setDados] = useState<FichaCadastral | null>(null)
  const [erro, setErro] = useState<string | null>(null)
  const [carregando, setCarregando] = useState(true)
  const [enviando, setEnviando] = useState(false)
  const [progresso, setProgresso] = useState(0)
  const [recado, setRecado] = useState<string | null>(null)

  const campoArquivo = useRef<HTMLInputElement | null>(null)

  useEffect(() => {
    let vivo = true
    chamar<FichaCadastral>('fichaDados')
      .then((linhas) => {
        if (!vivo) return
        const linha = linhas[0]
        if (!linha || linha.success !== 1) {
          setErro((linha as { message?: string })?.message ?? 'Não foi possível abrir a ficha.')
          return
        }
        setDados(linha)
      })
      .catch((e: Error) => {
        if (vivo) setErro(e.message)
      })
      .finally(() => {
        if (vivo) setCarregando(false)
      })
    return () => {
      vivo = false
    }
  }, [])

  async function enviar(arquivo: File) {
    setEnviando(true)
    setErro(null)
    setProgresso(0)
    try {
      const linha = exigirSucesso(
        await enviarArquivos<{ success?: number; message?: string }>(
          'enviarFicha',
          [{ arquivo, papel: 'ficha_assinada' }],
          setProgresso,
        ),
      )
      setRecado(linha.message ?? 'Ficha recebida.')
      setDados((d) => (d ? { ...d, statusFicha: 'Recebida', fichaMotivoRejeicao: null } : d))
    } catch (e) {
      setErro((e as Error).message)
    } finally {
      setEnviando(false)
      /* Limpa o input: sem isso, escolher o MESMO arquivo de novo depois de um
         erro não dispara `change` e o botão parece morto. */
      if (campoArquivo.current) campoArquivo.current.value = ''
    }
  }

  if (carregando) return <p className="painel__ajuda">Abrindo a ficha…</p>

  if (!dados) {
    return (
      <div className="grade">
        <p className="aviso aviso--erro" role="alert">
          {erro ?? 'Não foi possível abrir a ficha.'}
        </p>
        <div className="rodape">
          <button type="button" className="botao" onClick={aoVoltar}>
            Voltar
          </button>
        </div>
      </div>
    )
  }

  const podeEnviar = dados.statusFicha === 'Aguardando Assinatura' || dados.statusFicha === 'Rejeitada'

  const endereco = [dados.endereco, dados.numeroEndereco, dados.complemento]
    .filter(Boolean)
    .join(', ')

  /* Escopo Ficha já fica fora no servidor — a ficha não se cita. */
  const documentos = dados.documentos ?? []

  return (
    <div className="grade">
      {/* Tudo que é comando some na impressão: numa folha assinada, botão
          impresso é ruído que a pessoa tenta clicar. */}
      <div className="semimpressao grade">
        {recado ? (
          <p className="aviso aviso--ok" role="status">
            {recado}
          </p>
        ) : null}

        {erro ? (
          <p className="aviso aviso--erro" role="alert">
            {erro}
          </p>
        ) : null}

        {dados.statusFicha === 'Rejeitada' && dados.fichaMotivoRejeicao ? (
          <div className="aviso aviso--correcao">
            <h2 className="aviso__titulo">A ficha enviada não pôde ser aceita</h2>
            <p style={{ margin: 0 }}>{dados.fichaMotivoRejeicao}</p>
            <p className="aviso__nota">Corrija e envie de novo — o envio anterior é substituído.</p>
          </div>
        ) : null}

        {dados.statusFicha === 'Recebida' ? (
          <p className="aviso aviso--ok">
            Recebemos sua ficha assinada. Ela está em conferência.
          </p>
        ) : null}

        {dados.statusFicha === 'Validada' ? (
          <p className="aviso aviso--ok">Ficha conferida e aceita. Nada mais é necessário.</p>
        ) : null}

        <p className="painel__ajuda">
          Imprima, assine e envie de volta. Uma foto legível do documento assinado também serve.
        </p>

        <div className="rodape">
          <button type="button" className="botao" onClick={aoVoltar}>
            Voltar
          </button>
          <button type="button" className="botao" onClick={() => window.print()}>
            Imprimir
          </button>
          {podeEnviar ? (
            <>
              <input
                ref={campoArquivo}
                type="file"
                accept=".pdf,.jpg,.jpeg,.png"
                hidden
                onChange={(e) => {
                  const arquivo = e.target.files?.[0]
                  if (arquivo) void enviar(arquivo)
                }}
              />
              <button
                type="button"
                className="botao botao--principal"
                aria-disabled={enviando}
                onClick={() => campoArquivo.current?.click()}
              >
                {enviando ? `Enviando… ${Math.round(progresso * 100)}%` : 'Enviar ficha assinada'}
              </button>
            </>
          ) : null}
        </div>
      </div>

      {/* ------------------------------------------------------ a ficha --- */}
      <article className="ficha">
        {/* A LOGO vem da configuração, igual ao topo da página: trocar a marca é
            um UPDATE em `precad_Parametro`, sem build e sem republicar action.
            Precisa estar DENTRO da ficha porque a impressão esconde o topo — se
            ficasse só lá, a folha assinada sairia sem marca nenhuma.
            `alt` vazio: ao lado dela vem o mesmo nome em texto, e leitor de tela
            anunciando as duas seria repetição. */}
        <header className="ficha__cabeca">
          {logoUrl ? (
            <img
              className="ficha__logo"
              src={logoUrl}
              alt=""
              style={{ height: `${logoAltura ?? 40}px` }}
            />
          ) : (
            <span className="ficha__marca">
              Nazária <span>Distribuidora</span>
            </span>
          )}

          <div className="ficha__tituloArea">
            <h1 className="ficha__titulo">Ficha Cadastral</h1>
            <span className="ficha__numero">
              Solicitação nº {dados.numero ?? '—'}
              {dados.cnpj ? ` · CNPJ ${formatarCnpj(dados.cnpj)}` : ''}
            </span>
          </div>
        </header>

        <Bloco titulo="Empresa">
          <div className="ficha__grade">
            <Campo rotulo="Razão social" valor={dados.razaoSocial} largo />
            <Campo rotulo="Nome fantasia" valor={dados.nomeFantasia} />
            <Campo rotulo="Inscrição estadual" valor={dados.inscricaoEstadual} />
            <Campo rotulo="Tipo de cliente" valor={dados.tipoClienteOutro ?? dados.tipoCliente} />
            <Campo
              rotulo="Possui filiais"
              valor={
                dados.possuiFiliais === 1
                  ? `Sim${dados.quantidadeFiliais ? ` — ${dados.quantidadeFiliais}` : ''}`
                  : 'Não'
              }
            />
          </div>
        </Bloco>

        <Bloco titulo="Endereço e contato">
          <div className="ficha__grade">
            <Campo rotulo="Logradouro" valor={endereco} largo />
            <Campo rotulo="Bairro" valor={dados.bairro} />
            <Campo
              rotulo="Cidade / UF"
              valor={[dados.cidade, dados.uf].filter(Boolean).join(' / ')}
            />
            {/* Formatados na folha: o banco guarda só dígito (é o certo), mas
                "64000100" num documento que alguém vai conferir a olho é pior de
                ler do que "64000-100". */}
            <Campo rotulo="CEP" valor={dados.cep ? formatarCep(dados.cep) : null} />
            <Campo
              rotulo="Telefone"
              valor={dados.telefone ? formatarTelefone(dados.telefone) : null}
            />
            <Campo
              rotulo="WhatsApp"
              valor={dados.whatsapp ? formatarTelefone(dados.whatsapp) : null}
            />
            <Campo rotulo="E-mail" valor={dados.email} />
          </div>
        </Bloco>

        {/* Sócios em TABELA, e não como campos empilhados: com quatro campos por
            pessoa, dois sócios gastavam oito linhas da folha. Aqui gastam duas. */}
        <Bloco titulo="Quadro societário">
          {dados.socios.length === 0 ? (
            <p className="ficha__vazio">Nenhum sócio informado.</p>
          ) : (
            <table className="ficha__tabela">
              <thead>
                <tr>
                  <th className="ficha__col--n">#</th>
                  <th>Nome</th>
                  <th>CPF</th>
                  <th>Nascimento</th>
                  <th>E-mail</th>
                </tr>
              </thead>
              <tbody>
                {dados.socios.map((s, i) => (
                  <tr key={i}>
                    <td className="ficha__col--n">{i + 1}</td>
                    <td>{s.nome?.trim() ? s.nome : '—'}</td>
                    <td>{s.cpf ? formatarCpf(s.cpf) : '—'}</td>
                    <td>{isoParaBr(s.dataNascimento) || '—'}</td>
                    <td>{s.email?.trim() ? s.email : '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </Bloco>

        {/* Os arquivos enviados, por NOME e sem link: quem assina esta folha
            está declarando também QUE documentos mandou junto. Sem esta lista,
            a assinatura cobre só o que foi digitado, e a conferência de "o que
            chegou" fica sem contraparte no papel. */}
        <Bloco titulo="Documentos enviados">
          {/* `?? []` porque a action e o site sobem separados: com a
              `PreCadastro_Publico` antiga o campo não vem, e a ficha não pode
              virar tela branca por causa disso. */}
          {documentos.length === 0 ? (
            <p className="ficha__vazio">Nenhum arquivo enviado.</p>
          ) : (
            <table className="ficha__tabela">
              <thead>
                <tr>
                  <th>Documento</th>
                  <th>Referente a</th>
                  <th>Arquivo</th>
                </tr>
              </thead>
              <tbody>
                {documentos.map((d, i) => (
                  <tr key={i}>
                    <td>{d.tipo ?? '—'}</td>
                    <td>{d.socio ?? 'Empresa'}</td>
                    <td>{d.arquivo ?? '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </Bloco>

        {/* A REDAÇÃO ACEITA, não a vigente. É o que dá valor de prova à folha
            assinada — junto com o hash, a data e o IP registrados no aceite.
            `--texto` libera a quebra de página DENTRO do bloco: é o mais alto da
            folha, e mantê-lo indivisível era o que produzia meia página vazia. */}
        {dados.aceites.map((a, i) => (
          <section key={i} className="ficha__bloco ficha__bloco--texto">
            <h2 className="ficha__secao">
              {a.titulo} <span className="ficha__versao">versão {a.versao}</span>
            </h2>
            <div className="ficha__texto">{a.corpo}</div>
            <p className="ficha__prova">
              Aceito eletronicamente em{' '}
              {new Date(a.aceitoEm).toLocaleString('pt-BR', {
                day: '2-digit',
                month: '2-digit',
                year: 'numeric',
                hour: '2-digit',
                minute: '2-digit',
              })}
              {a.ip ? ` · IP ${a.ip}` : ''}
              {a.identificador ? ` · ${a.identificador}` : ''}
              {/* O hash em linha PRÓPRIA: emendado na frase, ele quebrava
                  deixando dois caracteres órfãos na linha seguinte. */}
              <span className="ficha__hash">verificação {a.hash}</span>
            </p>
          </section>
        ))}

        <section className="ficha__assinatura">
          <div className="ficha__risco" />
          <p className="ficha__legenda">
            {dados.razaoSocial ?? 'Assinatura'} — assinatura do representante legal
            <br />
            Local e data: ______________________________ , ____ / ____ / ________
          </p>
        </section>
      </article>
    </div>
  )
}
